/**
 * QL-375 — Mandate widget (group 4: the check, read-only).
 *
 * Laid out as design screens 01 / 02 / 06 (goal page) and 07 (portfolio
 * page), restyled by QL-664: one card titled "Mandate" with the basis table,
 * then the bordered "Permitted allocation" box — the title says what the dot
 * measures ("Portfolio" / "For purchases"), the header carries the
 * mandate's name, a mode chip, an "Existing mandate" chip for the bank's, and
 * the verdict badge; one row per line with its range bar; the legend with a
 * one-line verdict on its right. The accept-with-note decision box (screen
 * 02) follows; "Update mandate from this portfolio" (screen 07) sits in the
 * portfolio page's footer.
 *
 * `scope: "portfolio"` shows one portfolio's own weights. Under goal-level
 * blocking it carries an Informational badge: the goal decides, over all its
 * portfolios combined. Under portfolio-level blocking (group 7) the roles
 * swap: the portfolio page carries the verdict, the decision card and the
 * gate, and the goal page's combined view is informational with one verdict
 * line per portfolio. "Update mandate from this portfolio" (screen 07) sits
 * in the portfolio page's footer at both levels.
 */

import type {
  GoalMandateLineItem,
  MandateEvaluatedLine,
  MandateNotEvaluableReason,
  MandateScope,
  Translated,
} from "@repo/shared/types";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { getMandateState } from "@/lib/api/flow-instances/mandate";
import { goalSnapshotQueryKeys } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { useT } from "@/lib/i18n/use-t";
import {
  ApplyMandateDialog,
  MandateBasisTable,
  MandateDecisionSection,
  MandateLinesTable,
  MandatePortfolioSummary,
  MandateSelectionSection,
  MandateVerdictBadge,
  type MandateVerdictSentence,
} from "./mandate-widget/index";
import { T } from "./mandate-widget.i18n";

type MandateWidgetProps = {
  instanceId: string;
  goalId: string;
  /** "goal" (default) — the combined check; "portfolio" — one portfolio's own weights. */
  scope?: MandateScope;
  portfolioId?: string | null;
  config?: { style?: string; title?: string; description?: string };
  language: string;
  hasValidationError?: boolean;
  disabled?: boolean;
};

function notCheckedText(reason: MandateNotEvaluableReason, t: Translated<typeof T>): string {
  switch (reason) {
    case "no_mandate_required":
      return t.notEvaluableNoMandateRequired;
    case "no_risk_score":
      return t.notEvaluableNoRiskScore;
    case "no_snapshot":
      return t.notEvaluableNoSnapshot;
    case "missing_asset_data":
      return t.notEvaluableMissingAssetData;
    case "no_new_money":
      return t.notEvaluableNoNewMoney;
  }
}

/**
 * Before the goal can be evaluated the lines still show their bands, dot at 0.
 * `held` stays true: nothing was found unheld, nothing was looked at — the
 * verdict line says so instead of a "not held" after every instrument.
 */
function asUnevaluated(line: GoalMandateLineItem): MandateEvaluatedLine {
  return { ...line, combinedPct: 0, held: true, status: "within", unholdableReason: null };
}

export function MandateWidget({
  instanceId,
  goalId,
  scope = "goal",
  portfolioId = null,
  config,
  language,
  hasValidationError,
  disabled = false,
}: MandateWidgetProps) {
  const t = useT(T);
  // QL-664 — "Update portfolio from mandate": the Apply dialog, opened from
  // the deviation card's footer on the portfolio page.
  const [applyOpen, setApplyOpen] = useState(false);
  const queryKey =
    scope === "portfolio"
      ? ([...goalSnapshotQueryKeys.mandate(goalId), "portfolio", portfolioId] as const)
      : goalSnapshotQueryKeys.mandate(goalId);

  const {
    data: state,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => getMandateState(instanceId, goalId, scope, portfolioId),
  });

  if (isLoading) return null;

  if (isError || !state) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 flex items-center gap-2 text-sm text-destructive">
        {t.fetchError}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => refetch()}
        >
          {t.retry}
        </Button>
      </div>
    );
  }

  // Everything behind the collection switch and the goal type: nothing renders.
  if (!state.enabled) return null;

  const componentStyle = config?.style ?? "none";
  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : componentStyle === "border"
      ? "border rounded-lg p-4"
      : componentStyle === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  const ev = state.evaluation;
  const mandate = state.mandate;
  const portfolioScope = scope === "portfolio";

  // The card title and description: the designer's, else the screens' own.
  const header = (
    <FlowComponentHeader
      title={config?.title || t.mandate}
      description={config?.description || t.cardDescription}
    />
  );

  // Group 7 — which scope carries the gate here.
  const portfolioLevel = state.blockLevel === "portfolio";
  const informational = portfolioScope ? !portfolioLevel : portfolioLevel;

  // QL-677 — the Mandate selection section: the one place a template is
  // chosen (a goal never gets one by itself). Shown with or without a
  // mandate, on the goal page and the portfolio page alike.
  const selectionSection = (
    <MandateSelectionSection
      instanceId={instanceId}
      goalId={goalId}
      selection={state.selection}
      candidates={state.templateCandidates}
      mandate={mandate}
      basisPortfolioId={state.basisPortfolioId}
      portfolioId={portfolioScope ? portfolioId : null}
      disabled={disabled || !state.editable}
    />
  );

  // No mandate: the same box, headed "No mandate selected" — the portfolio's
  // own weights as rows when it holds products (QL-677), the selection
  // section, and the "Missing mandate" card (QL-664) on a required goal:
  // create one from this portfolio, or block where the gate sits.
  if (!mandate) {
    const missing = ev.notEvaluableReason === "no_mandate_required";
    const ownWeights: MandateEvaluatedLine[] = portfolioScope
      ? (state.updateFromPortfolio?.weights ?? []).map((w, i) => ({
          isin: w.isin,
          name: w.name,
          weightPct: null,
          minPct: null,
          defaultPct: null,
          maxPct: null,
          order: i,
          combinedPct: w.weightPct,
          held: true,
          status: "within",
          unholdableReason: null,
        }))
      : [];
    // Another cause than the missing mandate (no risk score, no forecast) is named.
    const cause =
      ev.notEvaluableReason && !missing ? notCheckedText(ev.notEvaluableReason, t) : null;
    return (
      <div className={wrapperClass}>
        {header}
        <div className="space-y-4 mt-3">
          <MandateLinesTable
            mode="fixed"
            lines={ownWeights}
            evaluated={false}
            noMandate
            unlisted={[]}
            scope={scope}
            titleSuffix={t.titlePortfolio}
            mandateName={t.noMandateSelected}
            chips={[]}
            badge={null}
            subHeader={
              // The portfolio page's hint only: `ownWeights` is always empty
              // in goal scope, and the text speaks of "the portfolio" (PR 688 review).
              portfolioScope && ownWeights.length === 0 ? (
                <span className="block space-y-0.5">
                  <span className="block text-sm font-medium text-foreground">
                    {t.noProductsNoMandateTitle}
                  </span>
                  <span className="block text-sm">{t.noProductsNoMandateText}</span>
                </span>
              ) : undefined
            }
            verdict={cause ? { text: cause, tone: "warning" } : undefined}
            selectionSection={selectionSection}
            decisionSection={
              missing ? (
                <MandateDecisionSection
                  variant="missing"
                  instanceId={instanceId}
                  goalId={goalId}
                  portfolioId={portfolioScope ? portfolioId : null}
                  stored={ev.decision}
                  updateFromPortfolio={portfolioScope ? (state.updateFromPortfolio ?? null) : null}
                  decisionsHere={!informational}
                  language={language}
                  disabled={disabled || !state.editable}
                />
              ) : undefined
            }
          />
        </div>
      </div>
    );
  }
  const badge = (
    <MandateVerdictBadge
      effective={ev.effective}
      notEvaluableReason={ev.notEvaluableReason}
      informational={informational}
    />
  );

  const breached = ev.effective === "deviation" || ev.effective === "not_accepted";
  const decided = ev.effective === "accepted" || ev.effective === "not_accepted";
  // Screen 02: the decision box sits under the verdict on the page that
  // carries the gate — the goal page under goal blocking, the portfolio page
  // under portfolio blocking — while it breaches (or a decision stands) and
  // the collection allows it.
  const showDecision = !informational && (breached || decided) && ev.deviationAllowed;
  // Nothing evaluated and no reason given: the goal has no decided portfolio
  // yet (rule 3 — the portfolio component's gap), so the bands show at 0.
  const unevaluated = ev.lines.length === 0 && !ev.notEvaluableReason;

  // QL-664 — what the dot measures. A one-time follow-up under the buy basis
  // is judged on its new money; everything else on its composition. The goal
  // page combines compositions (the rules refuse goal-level + buy).
  const judgedOnBuy =
    portfolioScope &&
    ev.basis.portfolios.some(
      (p) => (portfolioId ? p.goalPortfolioId === portfolioId : true) && p.basisKind === "buy",
    );
  const titleSuffix = judgedOnBuy ? t.titleBuy : t.titlePortfolio;

  // The one-line verdict on the legend row, by tone.
  const verdict: MandateVerdictSentence | undefined = ev.notEvaluableReason
    ? { text: notCheckedText(ev.notEvaluableReason, t), tone: "warning" }
    : breached || decided
      ? {
          text: judgedOnBuy ? t.breachBuy : portfolioScope ? t.breachPortfolio : t.breachGoal,
          tone: "breach",
        }
      : unevaluated
        ? { text: portfolioScope ? t.nothingToCheckPortfolio : t.nothingToCheck, tone: "muted" }
        : ev.effective === "ok"
          ? { text: portfolioScope ? t.inLinePortfolio : t.inLineGoal, tone: "ok" }
          : undefined;

  // QL-664 — "Update portfolio from mandate" sits in the deviation card's
  // footer on the portfolio page under portfolio-level blocking: Apply,
  // rewriting the portfolio to the mandate from its current total. Disabled
  // with Apply's own reason on a model / dynamic portfolio (QL-695: a
  // follow-up is allowed — its holdings become targets).
  // QL-677 — never on the mandate's basis portfolio: the portfolio IS the mandate.
  const applyAvail =
    portfolioScope && portfolioLevel && !state.updateFromPortfolio?.isBasis
      ? state.applyFromMandate
      : undefined;
  // QL-695 — a follow-up is no longer a reason: Apply sets its targets.
  // `goal_level_blocking` never reaches here: the footer is drawn under
  // portfolio-level blocking only (the service rule serves the AI and the API).
  // QL-730 A2 — a model portfolio switches to a matching model portfolio or is
  // refused with the ticket's message; a dynamic one is always refused.
  const applyUnavailable =
    applyAvail?.disabledReason === "not_custom"
      ? t.applyUnavailableNotCustom
      : applyAvail?.disabledReason === "no_templates"
        ? t.applyUnavailableNoTemplates
        : applyAvail?.disabledReason === "no_matching_model"
          ? t.applyUnavailableNoMatchingModel
          : applyAvail?.disabledReason === "dynamic"
            ? t.applyUnavailableDynamic
            : null;
  const judgedRow = portfolioId
    ? ev.basis.portfolios.find((p) => p.goalPortfolioId === portfolioId)
    : undefined;
  const applyFooter = applyAvail ? (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <Button
        type="button"
        variant="outline"
        className="shrink-0"
        disabled={disabled || !state.editable || !applyAvail.available}
        onClick={() => setApplyOpen(true)}
      >
        {t.updatePortfolioFromMandate}
      </Button>
      {applyUnavailable && (
        <span className="text-xs text-muted-foreground">{applyUnavailable}</span>
      )}
    </div>
  ) : undefined;

  // The header: the mandate's name (a portfolio-written one reads "Custom
  // mandate"), its mode, and "Existing mandate" when it is the bank's.
  const mandateName = mandate.source === "portfolio" ? t.customMandateName : mandate.name;
  const chips = [
    mandate.mode === "fixed" ? t.modeFixed : t.modeMinMax,
    ...(mandate.source === "bank" ? [t.chipExistingMandate] : []),
  ];

  return (
    <div className={wrapperClass}>
      {header}
      <div className="space-y-4 mt-3">
        {!portfolioScope && ev.basis.portfolios.length > 0 && (
          <MandateBasisTable basis={ev.basis} language={language} />
        )}

        <MandateLinesTable
          mode={mandate.mode}
          lines={ev.lines.length > 0 ? ev.lines : mandate.lines.map(asUnevaluated)}
          evaluated={ev.lines.length > 0}
          unlisted={ev.unlisted}
          scope={scope}
          titleSuffix={titleSuffix}
          mandateName={mandateName}
          chips={chips}
          badge={badge}
          subHeader={
            state.existingMandate ? t.replacesAtBank(state.existingMandate.name) : undefined
          }
          verdict={verdict}
          selectionSection={selectionSection}
          decisionSection={
            showDecision ? (
              <MandateDecisionSection
                variant="deviation"
                instanceId={instanceId}
                goalId={goalId}
                portfolioId={portfolioScope ? portfolioId : null}
                stored={ev.decision}
                // QL-664 — the update option comes first, disabled with the
                // server's reason: this portfolio's weights on the portfolio
                // page, the goal's combined allocation on the goal page.
                // QL-677 — on the portfolio page the create option is offered
                // only while the mandate is portfolio-created — or, QL-695, the
                // bank's (the proposal replaces it); a selected template leaves
                // Accept and Block (and the footer).
                updateFromPortfolio={
                  portfolioScope && (mandate.source === "portfolio" || mandate.source === "bank")
                    ? (state.updateFromPortfolio ?? null)
                    : null
                }
                replacesExisting={mandate.source === "bank"}
                updateFromGoal={portfolioScope ? null : (state.updateFromGoal ?? null)}
                decisionsHere={!informational}
                language={language}
                // The wizard's flag moves the moment the instance is submitted
                // in this tab; the state's flag covers a page opened later.
                disabled={disabled || !state.editable}
                footer={applyFooter}
              />
            ) : breached && !informational && !ev.deviationAllowed ? (
              // QL-695 — a breach the collection does not allow to be accepted
              // (e.g. a recurring portfolio with recurring deviations off) draws
              // no decision card. QL-730 Q4 — it says so in words, naming the
              // switch (one-time, recurring, or both), where the card would sit;
              // "Update portfolio from mandate" is the remedy, so the footer
              // follows when available.
              <div className="border rounded-lg p-4 space-y-3">
                <p className="text-sm text-destructive">
                  {ev.deviationBlockedBy === "recurring"
                    ? t.deviationBlockedRecurring
                    : ev.deviationBlockedBy === "both"
                      ? t.deviationBlockedBoth
                      : ev.deviationBlockedBy === "one_time"
                        ? t.deviationBlockedOneTime
                        : // A snapshot frozen before QL-730 names no switch (PR 701 review).
                          t.deviationBlockedGeneric}{" "}
                  {portfolioScope
                    ? t.deviationBlockedRemedyPortfolio
                    : t.deviationBlockedRemedyGoal}
                </p>
                {applyFooter}
              </div>
            ) : undefined
          }
        />

        {portfolioScope ? (
          <p className="text-sm text-muted-foreground">
            {portfolioLevel ? t.portfolioFooterPortfolioLevel : t.portfolioFooter}
          </p>
        ) : (
          portfolioLevel && (
            <>
              <MandatePortfolioSummary verdicts={state.portfolioVerdicts ?? []} />
              <p className="text-sm text-muted-foreground">{t.goalFooterPortfolioLevel}</p>
            </>
          )
        )}
      </div>

      {applyAvail && portfolioId && (
        <ApplyMandateDialog
          open={applyOpen}
          onOpenChange={setApplyOpen}
          instanceId={instanceId}
          goalId={goalId}
          portfolioId={portfolioId}
          // QL-677 — the footer rewrites the portfolio to the goal's mandate, locked.
          mode="mandate"
          mandate={mandate}
          perDeposit={judgedRow?.scheduleType === "recurring"}
          frequency={
            judgedRow?.scheduleType === "recurring"
              ? (judgedRow.recurringFrequency ?? "monthly")
              : null
          }
          initialAmount={applyAvail.currentTotal}
          replaces={applyAvail.replaces}
          // QL-695 — an account follow-up: targets and full sells, the copy says so.
          followup={judgedRow?.isFollowup ? "account" : null}
          // QL-730 A2 — a model portfolio: the matching model portfolios to switch to.
          // PR 701 review — the server's answer, not the basis: a model
          // portfolio with no value yet is not in the basis but is still one.
          modelMatches={
            applyAvail.modelMatches.length > 0 || applyAvail.disabledReason === "no_matching_model"
              ? applyAvail.modelMatches
              : null
          }
          currentModelPortfolioId={applyAvail.currentModelPortfolioId}
        />
      )}
    </div>
  );
}
